import React, { useState } from 'react'
import { Autocomplete, Box, Chip, MenuItem, TextField, Typography } from '@mui/material'
import { DatePickerSeeker } from '@ui-seeker/core'

import { BACKEND_APIS, SERVICES_TYPE } from '@constants'
import { dsColorNew } from '@palette'
import { FieldNote, KindPillsField } from '@components'
import { fmtDay, fmtWeekday, fromIso, toIso } from '../../../@services/dates'
import { kindPills, menuTab, replaceNote, shortList } from '../../../../../../@components/KindPillsField/kindPills'
import useOptions from '../@hooks/useOptions'


const BI = { service_type: SERVICES_TYPE.BI }
const MAX_DATES = 31

/** A multi-pick list inside a pills menu: the options of one kind; picking here replaces another kind */
function PickList({ label, options, value, onChange }) {
	const byValue = new Map((options || []).map(o => [String(o.value), o]))
	const picked = value.map(v => byValue.get(String(v))).filter(Boolean)
	return <Autocomplete multiple size="small" disableCloseOnSelect loading={options === null} options={options || []}
		value={picked} onChange={(_, list) => onChange(list.map(o => o.value))}
		isOptionEqualToValue={(a, b) => String(a.value) === String(b.value)} getOptionLabel={o => o?.label || ''}
		renderInput={params => <TextField {...params} autoFocus label={label} placeholder={picked.length ? undefined : 'Search…'}/>}/>
}

const labelsOf = (options, ids) => {
	const byValue = new Map((options || []).map(o => [String(o.value), o.label]))
	return ids.map(id => byValue.get(String(id)) || String(id))
}

/** "Offices": one field with pills Offices · Office groups (P1, P2); a group is resolved by the server with its owner */
export function OfficesField({ view, offices, onChange }) {
	const groups = useOptions(`${BACKEND_APIS.CRUD}/user/${SERVICES_TYPE.OFFICE_GROUP}?selectorFormat=true`)
	const LABELS = { offices: 'Offices', groups: 'Office groups' }
	const options = { offices: (offices || []).map(o => ({ value: Number(o.value), label: o.label })), groups: (groups || []).map(g => ({ value: Number(g.value), label: g.label })) }
	const labels = labelsOf(options[view.okind], view.offices)
	const on = !!view.offices.length
	const current = { kind: view.okind, on }
	return <KindPillsField
		label="Offices" allText="Pick offices" on={on} labels={labels} pillsLabel="Offices or office groups"
		under={on ? (view.okind === 'groups' ? `Group: ${shortList(labels)}` : `${view.offices.length} office${view.offices.length === 1 ? '' : 's'}`) : 'One row per office in the table'}
		kinds={kindPills(['offices', 'groups'], LABELS, { groups: groups ? groups.length : null })}
		openTab={() => menuTab(current, 'offices')}
		noteFor={tab => replaceNote(current, tab, labels)}
		renderKind={tab => <PickList label={LABELS[tab]} options={tab === 'groups' ? (groups ? options.groups : null) : options.offices}
			value={tab === view.okind ? view.offices : []}
			onChange={ids => onChange({ okind: tab, offices: ids.slice(0, 50) })}/>}
		onClear={() => onChange({ okind: 'offices', offices: [] })} clearLabel="No offices"
	/>
}

/** "Sites": one field with pills Sites · Site groups · Markets (P9); nothing picked = every site */
export function SitesField({ view, onChange }) {
	const sites = useOptions(`${BACKEND_APIS.MASTER_DATA}/options/site`, BI)
	const groups = useOptions(`${BACKEND_APIS.CRUD}/user/${SERVICES_TYPE.SITE_GROUP}?selectorFormat=true`)
	const markets = useOptions(`${BACKEND_APIS.MASTER_DATA}/options/markets`, BI)
	const LABELS = { sites: 'Sites', siteGroups: 'Site groups', markets: 'Markets' }
	const lists = {
		sites: sites && sites.map(s => ({ value: Number(s.value), label: s.label })),
		siteGroups: groups && groups.map(g => ({ value: Number(g.value), label: g.label })),
		markets: markets && markets.map(m => ({ value: String(m.value), label: m.label || String(m.value) })),
	}
	const labels = labelsOf(lists[view.skind], view.sites)
	const on = !!view.sites.length
	const current = { kind: view.skind, on }
	return <KindPillsField
		label="Sites" allText="All sites" on={on} labels={labels} pillsLabel="Sites, site groups or markets"
		under={on ? `${LABELS[view.skind]}: ${shortList(labels)}` : 'Every site that captured prices'}
		kinds={kindPills(['sites', 'siteGroups', 'markets'], LABELS, { siteGroups: groups ? groups.length : null })}
		openTab={() => menuTab(current, 'sites')}
		noteFor={tab => replaceNote(current, tab, labels)}
		renderKind={tab => <PickList label={LABELS[tab]} options={lists[tab]} value={tab === view.skind ? view.sites : []}
			onChange={ids => onChange({ skind: tab, sites: ids })}/>}
		onClear={() => onChange({ skind: 'sites', sites: [] })} clearLabel="All sites"
	/>
}

/** "Pick-up dates": several dates, one column each (P3) */
export function PickupDatesField({ dates, onChange }) {
	const [adding, setAdding] = useState(false)
	const add = d => {
		if (!(d instanceof Date) || Number.isNaN(d.getTime())) return
		onChange([...new Set([...dates, toIso(d)])].sort().slice(0, MAX_DATES))
		setAdding(false)
	}
	return <Box>
		<TextField size="small" fullWidth label="Pick-up dates" value={dates.length ? '' : 'Pick one or more dates'} onClick={() => setAdding(true)}
			InputLabelProps={{ shrink: true }}
			InputProps={{
				readOnly: true, sx: { cursor: 'pointer', flexWrap: 'wrap', gap: 0.5, py: dates.length ? 0.5 : 0 },
				startAdornment: dates.length ? <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', py: 0.25 }}>
					{dates.map(d => <Chip key={d} size="small" label={fmtWeekday(fromIso(d), false)} onDelete={() => onChange(dates.filter(x => x !== d))}/>)}
				</Box> : null,
			}}
			inputProps={{ 'aria-label': 'Pick-up dates', style: { width: dates.length ? 0 : undefined, cursor: 'pointer' } }}/>
		{adding && <Box sx={{ mt: 1 }}><DatePickerSeeker label="Add a pick-up date" shouldDisableDate={() => false} value={null} onChange={add}/></Box>}
		<FieldNote>{dates.length ? `${dates.length} date${dates.length === 1 ? '' : 's'} · one column each` : 'Up to 31 dates'}</FieldNote>
	</Box>
}

/** "Capture weeks": the week in progress (marked) and the 4 complete ones; by default the 4 complete (P4, decision 12) */
export function CaptureWeeksField({ weeks, options, onChange }) {
	const list = options || []
	const value = weeks === 'default' ? list.filter(o => !o.inProgress).map(o => o.key) : weeks
	const label = o => `${o.label} · ${fmtDay(fromIso(o.from), false)}–${fmtDay(fromIso(o.to), false)}${o.inProgress ? ' · in progress' : ''}`
	const picked = list.filter(o => value.includes(o.key))
	return <Box>
		<TextField select size="small" fullWidth label="Capture weeks" value={value}
			SelectProps={{
				multiple: true,
				renderValue: () => (weeks === 'default' ? 'Last 4 weeks' : picked.length ? `${picked[0].label}${picked.length > 1 ? ` – ${picked.at(-1).label}` : ''} · ${picked.length} week${picked.length === 1 ? '' : 's'}` : 'Pick weeks'),
			}}
			onChange={e => {
				const keys = [...e.target.value].sort()
				if (keys.length) onChange(keys)
			}}
		>
			{!list.length && <MenuItem disabled value="">Loading…</MenuItem>}
			{list.map(o => <MenuItem key={o.key} value={o.key} sx={{ fontSize: 14 }}>{label(o)}</MenuItem>)}
			<Typography component="li" sx={{ px: 2, py: 1, fontSize: 12, color: dsColorNew.textMuted, maxWidth: 320 }}>
				Weeks run Sunday to Saturday. The week in progress is not complete yet.
			</Typography>
		</TextField>
		<FieldNote>{picked.length ? `${fmtDay(fromIso(picked[0].from), false)} – ${fmtDay(fromIso(picked.at(-1).to))}` : ''}</FieldNote>
	</Box>
}

/** "Transmission" (P11) */
export function TransmissionField({ value, onChange }) {
	const options = useOptions(`${BACKEND_APIS.MASTER_DATA}/options/transmission`, BI)
	const list = (options || []).map(o => ({ value: Number(o.value), label: o.label }))
	const picked = list.filter(o => value.includes(o.value))
	return <Box>
		<Autocomplete multiple size="small" options={list} value={picked} loading={options === null}
			onChange={(_, l) => onChange(l.map(o => o.value))} isOptionEqualToValue={(a, b) => a.value === b.value}
			renderInput={params => <TextField {...params} label="Transmission" placeholder={picked.length ? undefined : 'Any'}/>}/>
		<FieldNote>{picked.length ? picked.map(o => o.label).join(', ') : 'Manual and automatic'}</FieldNote>
	</Box>
}

/** "Currency" (P13): only the prices the site gave in it, not converted */
export function CurrencyField({ value, options, onChange }) {
	const list = options || []
	const current = list.find(o => Number(o.value) === Number(value))
	return <Box>
		<TextField select size="small" fullWidth label="Currency" value={current ? current.value : ''} onChange={e => onChange(Number(e.target.value))}>
			{list.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
		</TextField>
		<FieldNote>{current ? `Only prices the site gave in ${current.label} · not converted` : ''}</FieldNote>
	</Box>
}
